/**
 * Polymarket CLOB API 认证服务
 * 基于 xguess-web 实现迁移
 */
import { ClobClient, SignatureTypeV2, type OpenOrder, type ApiKeyCreds } from '@polymarket/clob-client-v2';
import { ethers } from 'ethers5';

// 重新导出官方类型
export type { OpenOrder as ClobOrder, ApiKeyCreds };

// Polymarket CLOB API 地址
// 默认 V1 生产 host(切换日 Polymarket 内部把 clob.polymarket.com 切到 V2,我们不用动)
// debug 可在 Account Settings 设置 override(localStorage 持久化),刷新页面生效
const DEFAULT_CLOB_HOST = 'https://clob.polymarket.com';

function readClobHostOverride(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('predict-store');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.clobHostOverride || null;
  } catch {
    return null;
  }
}

export const CLOB_API_URL = readClobHostOverride() || DEFAULT_CLOB_HOST;

// V2 builder code (bytes32),链上公开 attribution 标识
// V2 SDK 通过 builderConfig 自动附加到订单 builder 字段;Phase 2a.7 接入到 createAuthenticatedClobClient
export const BUILDER_CODE = '0x4b8a17c8f3e0d0986bab88d1debaa4c26443b37ad67295107032c8bd153f6699';

// Polygon 链 ID
const POLYGON_CHAIN_ID = 137;

// API 代理基础 URL
const API_BASE = process.env.NEXT_PUBLIC_PREDICT_API_URL || '/api';

// Signer 类型（使用 ethers5 的抽象 Signer 类型）
type Signer = ethers.Signer;

/** Privy signTypedData 函数类型 */
type PrivySignTypedData = (
  typedData: {
    domain: any;
    types: any;
    primaryType: string;
    message: any;
  },
  options?: {
    address?: string;
    uiOptions?: { showWalletUIs?: boolean };
  }
) => Promise<{ signature: string }>;

/** Privy signMessage 函数类型 */
type PrivySignMessage = (
  message: string | Uint8Array,
  options?: {
    address?: string;
    uiOptions?: { showWalletUIs?: boolean };
  }
) => Promise<{ signature: string }>;

/** Privy signMessage hook 返回的函数类型 */
type PrivySignMessageFn = (
  config: { message: string },
  options?: {
    address?: string;
    uiOptions?: { showWalletUIs?: boolean };
  }
) => Promise<{ signature: string }>;

/**
 * 递归将对象中的 BigInt 转换为字符串
 * Privy SDK 无法序列化 BigInt，需要转换
 */
function convertBigIntToString(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'bigint') return obj.toString();
  if (Array.isArray(obj)) return obj.map(convertBigIntToString);
  if (typeof obj === 'object') {
    const result: Record<string, any> = {};
    for (const key of Object.keys(obj)) {
      result[key] = convertBigIntToString(obj[key]);
    }
    return result;
  }
  return obj;
}

/**
 * 从 EIP-712 types 对象中提取 primaryType
 * primaryType 是唯一不被其他类型引用的顶级类型
 */
function extractPrimaryType(types: Record<string, any[]>): string {
  const allTypes = Object.keys(types).filter(t => t !== 'EIP712Domain');
  const referencedTypes = new Set<string>();

  for (const typeName of allTypes) {
    const fields = types[typeName];
    if (Array.isArray(fields)) {
      for (const field of fields) {
        const baseType = field.type.replace(/\[\]$/, '');
        if (allTypes.includes(baseType)) {
          referencedTypes.add(baseType);
        }
      }
    }
  }

  const primaryTypes = allTypes.filter(t => !referencedTypes.has(t));
  return primaryTypes[0] || allTypes[0];
}

/**
 * 创建无弹窗签名的 Signer 包装器
 * 通过 Proxy 拦截 _signTypedData 和 signMessage 调用，使用 Privy 的无弹窗签名
 */
export function createSilentSigner(originalSigner: Signer, privySignTypedData: PrivySignTypedData, privySignMessage?: PrivySignMessageFn): Signer {
  console.log('[createSilentSigner] Creating proxy with privySignMessage:', !!privySignMessage);
  return new Proxy(originalSigner, {
    get(target, prop, receiver) {
      // 调试：打印所有被访问的属性
      if (prop === 'signMessage' || prop === '_signTypedData') {
        console.log('[SilentSigner] Property accessed:', prop);
      }
      // 拦截 _signTypedData 方法
      if (prop === '_signTypedData') {
        return async (domain: any, types: any, value: any): Promise<string> => {
          const primaryType = extractPrimaryType(types);
          console.log('[SilentSigner] Signing typed data silently, primaryType:', primaryType);

          // 获取原始 signer 地址，并传递给 Privy 确保使用正确的钱包签名
          let signerAddress: string | undefined;
          try {
            signerAddress = await target.getAddress();
            console.log('[SilentSigner] Using signer address:', signerAddress);
          } catch (e) {
            console.error('[SilentSigner] Failed to get signer address:', e);
          }

          // 转换 BigInt 为字符串，避免 Privy SDK 序列化失败
          const safeDomain = convertBigIntToString(domain);
          const safeValue = convertBigIntToString(value);

          const result = await privySignTypedData(
            { domain: safeDomain, types, primaryType, message: safeValue },
            {
              address: signerAddress,
              uiOptions: { showWalletUIs: false },
            }
          );

          const sig = result.signature;
          console.log('[SilentSigner] Signature received, length:', sig?.length);

          if (!sig || sig.length !== 132) {
            console.warn('[SilentSigner] Unexpected signature length:', sig?.length, '(expected 132)');
          }

          return sig;
        };
      }

      // 拦截 signMessage 方法（用于 personal_sign）
      // Safe SDK 需要签名原始 bytes，必须使用 provider 直接调用
      if (prop === 'signMessage') {
        console.log('[SilentSigner] signMessage intercepted');

        return async (message: string | Uint8Array): Promise<string> => {
          console.log('[SilentSigner] Signing message via provider (raw bytes)');

          // 将消息转换为 hex 格式
          let messageHex: string;
          if (message instanceof Uint8Array) {
            messageHex = ethers.utils.hexlify(message);
          } else if (typeof message === 'string' && !message.startsWith('0x')) {
            // 普通字符串转 hex
            messageHex = ethers.utils.hexlify(ethers.utils.toUtf8Bytes(message));
          } else {
            messageHex = message;
          }

          const signerAddress = await target.getAddress();
          console.log('[SilentSigner] Message hex:', messageHex.slice(0, 20) + '...', 'address:', signerAddress);

          // 获取底层 provider 并直接调用 personal_sign
          // Privy embedded wallet 的 provider.request 支持静默签名
          const provider = (target as any).provider;
          if (provider && typeof provider.request === 'function') {
            try {
              // 直接调用 personal_sign，Privy embedded wallet 默认静默
              const sig = await provider.request({
                method: 'personal_sign',
                params: [messageHex, signerAddress],
              });
              console.log('[SilentSigner] Signature received via provider, length:', sig?.length);
              return sig;
            } catch (err: any) {
              console.error('[SilentSigner] Provider personal_sign failed:', err.message);
            }
          }

          // Fallback: 使用原始 signer
          console.log('[SilentSigner] Fallback to original signMessage');
          const originalSignMessage = Reflect.get(target, 'signMessage', receiver);
          return originalSignMessage.call(target, message);
        };
      }

      // 其他方法保持原样
      const value = Reflect.get(target, prop, receiver);
      if (typeof value === 'function') {
        return value.bind(target);
      }
      return value;
    },
  }) as Signer;
}

/**
 * 派生用户 API 凭证（需要用户签名）
 * 注意：此函数不再内部保存凭证，调用者需要自行通过 Zustand store 保存
 */
export async function deriveUserCredentials(signer: Signer): Promise<ApiKeyCreds> {
  console.log('[ClobAPI] Deriving new API credentials...');

  // 调试：打印 signer 地址
  try {
    const signerAddress = await signer.getAddress();
    console.log('[ClobAPI] Signer address:', signerAddress);
  } catch (e) {
    console.error('[ClobAPI] Failed to get signer address:', e);
  }

  // 创建临时 ClobClient（带 signer，无凭证）用于派生
  // V2 SDK 的 ClobSigner 联合类型对 ethers v5 Signer 鸭子兼容但 TS 严匹不通过,inline 断言
  const tempClient = new ClobClient({
    host: CLOB_API_URL,
    chain: POLYGON_CHAIN_ID,
    signer: signer as never,
  });

  // derive 优先：先尝试获取已有 key，失败再创建新 key
  // 这样多端（App/Web）会通过 derive 拿到相同的 key，不会互相踢
  console.log('[ClobAPI] Calling deriveApiKey first (derive-first strategy)...');

  let creds;
  try {
    creds = await tempClient.deriveApiKey();
    // SDK 在 400 时不抛异常，会返回空对象。主动校验，无效则触发 fallback 创建新 key。
    // 首次用户场景：从未在 Polymarket 创建过 API key，derive 必然返回空，要 fallback 到 create。
    if (!creds || !creds.key || !creds.secret || !creds.passphrase) {
      throw new Error('derive_returned_empty');
    }
    console.log('[ClobAPI] Derived existing API key');
  } catch (deriveErr: any) {
    console.log('[ClobAPI] Derive 失败/无效，fallback 到 createApiKey:', deriveErr?.message);
    try {
      creds = await tempClient.createApiKey();
      console.log('[ClobAPI] Created new API key');
    } catch (createErr: any) {
      const apiError = createErr?.response?.data?.error || createErr?.message || 'Unknown error';
      console.error('[ClobAPI] Both derive and create failed:', apiError);
      throw new Error(apiError);
    }
  }

  // 验证凭证完整性
  if (!creds || !creds.key || !creds.secret || !creds.passphrase) {
    console.error('[ClobAPI] Invalid credentials received:', {
      hasKey: !!creds?.key,
      hasSecret: !!creds?.secret,
      hasPassphrase: !!creds?.passphrase,
    });
    throw new Error('Failed to obtain CLOB API credentials from Polymarket');
  }

  console.log('[ClobAPI] API credentials derived successfully');
  return creds;
}

/**
 * 创建认证的 ClobClient（用于签名和提交订单）
 * @param signer - EOA 签名者
 * @param creds - API 凭证
 * @param funderAddress - Safe 钱包地址（资金所在地址）
 */
export function createAuthenticatedClobClient(signer: Signer, creds: ApiKeyCreds, funderAddress?: string): ClobClient {
  // 如果有 funderAddress，说明使用 Safe 钱包，signatureType 设为 SAFE；否则 EOA
  // V2 SDK 枚举名是 POLY_GNOSIS_SAFE(= 2),与 V1 一致;不能写成 SAFE(undefined → 兜底成 EOA → Safe 验签 fail)
  const signatureType = funderAddress ? SignatureTypeV2.POLY_GNOSIS_SAFE : SignatureTypeV2.EOA;
  console.log('[ClobAPI] Creating authenticated client with signatureType:', signatureType);

  // V2 SDK 用 options 对象构造;builderConfig 接入 BUILDER_CODE 实现 V2 attribution
  // signer 断言:ethers v5 Signer 与 SDK 的 EthersSigner 鸭子兼容,但 TS 严匹不过
  return new ClobClient({
    host: CLOB_API_URL,
    chain: POLYGON_CHAIN_ID,
    signer: signer as never,
    creds,
    signatureType,
    funderAddress,
    builderConfig: { builderCode: BUILDER_CODE },
  });
}

/**
 * 获取用户未成交订单
 */
export async function getUserOpenOrders(clobClient: ClobClient): Promise<OpenOrder[]> {
  try {
    const response = await clobClient.getOpenOrders();
    return response || [];
  } catch (error) {
    console.error('[ClobAPI] Failed to get open orders:', error);
    return [];
  }
}

/**
 * 获取单个订单状态
 */
export async function getOrderStatus(clobClient: ClobClient, orderId: string): Promise<OpenOrder | null> {
  try {
    const order = await clobClient.getOrder(orderId);
    return order;
  } catch (error) {
    console.error('[ClobAPI] Failed to get order status:', error);
    return null;
  }
}

// 订单状态类型
export type OrderStatus = 'LIVE' | 'MATCHED' | 'CANCELLED' | 'EXPIRED' | 'UNKNOWN';

/**
 * 等待订单成交（轮询）
 */
export async function waitForOrderFill(
  clobClient: ClobClient,
  orderId: string,
  options?: {
    maxWaitMs?: number;
    pollIntervalMs?: number;
    onStatusChange?: (status: OrderStatus, order: OpenOrder | null) => void;
  }
): Promise<{ filled: boolean; order: OpenOrder | null }> {
  const maxWait = options?.maxWaitMs ?? 30000;
  const pollInterval = options?.pollIntervalMs ?? 2000;
  const startTime = Date.now();

  while (Date.now() - startTime < maxWait) {
    const order = await getOrderStatus(clobClient, orderId);

    if (!order) {
      // 订单可能已完全成交并从列表中移除
      options?.onStatusChange?.('MATCHED', null);
      return { filled: true, order: null };
    }

    const status = order.status?.toUpperCase() as OrderStatus;
    options?.onStatusChange?.(status, order);

    if (status === 'MATCHED') {
      return { filled: true, order };
    }

    if (status === 'CANCELLED' || status === 'EXPIRED') {
      return { filled: false, order };
    }

    // 检查是否部分成交
    const originalSize = parseFloat(order.original_size || '0');
    const sizeMatched = parseFloat(order.size_matched || '0');
    if (sizeMatched >= originalSize && originalSize > 0) {
      return { filled: true, order };
    }

    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }

  const finalOrder = await getOrderStatus(clobClient, orderId);
  return { filled: false, order: finalOrder };
}

/**
 * 取消订单
 */
export async function cancelOrder(clobClient: ClobClient, orderId: string): Promise<boolean> {
  try {
    await clobClient.cancelOrder({ orderID: orderId });
    console.log('[ClobAPI] Order cancelled:', orderId);
    return true;
  } catch (error) {
    console.error('[ClobAPI] Failed to cancel order:', error);
    return false;
  }
}

/**
 * 取消所有订单
 */
export async function cancelAllOrders(clobClient: ClobClient): Promise<boolean> {
  try {
    await clobClient.cancelAll();
    console.log('[ClobAPI] All orders cancelled');
    return true;
  } catch (error) {
    console.error('[ClobAPI] Failed to cancel all orders:', error);
    return false;
  }
}
